<script setup lang="ts">
/**
 * 属性「数据」Tab 的轻量绑定抽屉（线 J，docs/连接数据改造方案.md §3.3 / §4.3）。
 * 选服务 → 选设备 → **按选中设备的真实点位**批量勾选（可改显示名/单位）→ 保存。
 * 不再进完整向导、不再填密码（登录在「项目连接」）。
 *
 * 2026-08 改造（按设备点位目录）：点位清单不再取自服务全局 sourceShape.fieldMappings，
 * 而是「拉取列表」时从整包返回抽出每台设备的真实 attr1 点位（enrichDevicesWithPoints），
 * 选中 M0018 就只显示 M0018 的点；保存时把选中的点登记回服务 fieldMappings
 * （ensureDeviceFieldMappings，按设备类型/设备号匹配），保证运行时按设备取到数。
 * 卡片字段上限：MAX_CARD_FIELDS（用户拍板 5），可用「全选/清空」批量勾。
 */
import { computed, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { storeToRefs } from 'pinia'
import { useSceneStore } from '@/stores/scene'
import { useConnectDataEditorStore } from '@/stores/connectDataEditor'
import { buildLiveDataKey, type DataService, type SelectedMetricEntry } from '@/types/connectData'
import type { SceneObject } from '@/types/scene'
import { buildInfoCardAndBindings, ensureDeviceFieldMappings } from '@/connect-data/applyConnectDataWizard'
import { fetchMergedRaw } from '@/runtime/source-gateway/fetchSnapshots'
import {
  enrichDevicesWithPoints,
  deviceByCode,
  MAX_CARD_FIELDS,
  repairObjectBindingsToChineseNames,
} from '@/connect-data/perDevicePoints'
import { registry } from '@/components-lib/registry'

const props = defineProps<{
  visible: boolean
  objectId: string
  /** 保存/解除绑定前推一次历史（撤销栈） */
  onHistoryPush?: () => void
}>()
const emit = defineEmits<{ close: []; openProjectConnect: [] }>()

const sceneStore = useSceneStore()
const editorStore = useConnectDataEditorStore()
const { connectLoginStatus: status } = storeToRefs(sceneStore)
const isLoggedIn = computed(() => status.value === 'logged-in')

const services = computed<DataService[]>(() => sceneStore.getDataServices())

const obj = computed<SceneObject | null>(() =>
  sceneStore.activeObjects().find((o) => o.id === props.objectId) ?? null,
)

/** 组件定义：用于判断该对象类型是否支持「货位占用率」绑定（如立体仓储货架） */
const compDef = computed(() => (obj.value ? registry.get(obj.value.type) : undefined))

/** 当前绑定的服务（对象 dataServiceId 或缺省首个） */
const boundServiceId = computed(() => {
  const o = obj.value
  if (!o) return ''
  return o.dataServiceId ?? services.value[0]?.id ?? ''
})

/** 当前绑定的设备编号 */
const boundAssetCode = computed(() => obj.value?.assetCode ?? '')

const serviceId = ref('')
const assetCode = ref('')
/**
 * 选中设备的全量点位目录（来自缓存的真实点位）。每项：
 * key=attr1 点号、name=中文名、displayName=卡片显示名、unit=单位、checked=是否勾选上卡片。
 */
const devicePoints = ref<Array<{ key: string; name: string; displayName: string; unit?: string; checked: boolean }>>([])
const loadingDevices = ref(false)
const devicesError = ref('')

/** 货位占用率绑定草稿（仅 compDef.supportsCrateFillBinding 时展示） */
const crateFillMetricKey = ref('')
const crateFillMin = ref(0)
const crateFillMax = ref(100)

watch(
  () => props.visible,
  (v) => {
    if (!v) return
    serviceId.value = boundServiceId.value
    assetCode.value = boundAssetCode.value
    devicePoints.value = seedFromCache(serviceId.value, assetCode.value, obj.value?.dataBindings)
    const cf = obj.value?.dataBindings?.crateFill
    crateFillMetricKey.value = cf?.metricKey ?? ''
    crateFillMin.value = cf?.min ?? 0
    crateFillMax.value = cf?.max ?? 100
    devicesError.value = ''
  },
)

/** 当前选服务 */
const selectedService = computed<DataService | undefined>(() =>
  services.value.find((s) => s.id === serviceId.value),
)

/** 设备下拉：本会话试拉缓存（含每台真实点位，仅用 assetCode/name 渲染） */
const deviceOptions = computed<Array<{ assetCode: string; name: string }>>(() => {
  const cached = editorStore.getDevicesCache(serviceId.value)
  return cached?.devices ?? []
})

const selectedCount = computed(() => devicePoints.value.filter((p) => p.checked).length)

/** 从缓存里取出指定设备的全量点位；已绑定的点位保持勾选，其余不勾（用户批量勾） */
function seedFromCache(sid: string, code: string, existing?: SceneObject['dataBindings']): typeof devicePoints.value {
  const cached = editorStore.getDevicesCache(sid)
  const dev = cached ? deviceByCode(cached.devices, code) : undefined
  if (!dev?.points?.length) return []
  // 已绑字段可能是中文名，也可能是历史误写的点号 —— 两种都能勾上
  const bound = new Set(
    (existing?.infoCard?.fields ?? []).map((f) => f.metricKey || f.fieldKey).filter(Boolean) as string[],
  )
  return dev.points.map((p) => ({
    key: p.key,
    name: p.name,
    displayName: p.name,
    ...(p.unit ? { unit: p.unit } : {}),
    checked: bound.has(p.name) || bound.has(p.key),
  }))
}

function onServiceChange() {
  assetCode.value = ''
  devicePoints.value = []
  crateFillMetricKey.value = ''
  devicesError.value = ''
}

/** 设备下拉切换：按新设备重新出全量点位；仅当仍选当前已绑设备时保留已勾选 */
function onAssetChange() {
  devicePoints.value = seedFromCache(
    serviceId.value,
    assetCode.value,
    assetCode.value === boundAssetCode.value ? obj.value?.dataBindings : undefined,
  )
  crateFillMetricKey.value = ''
}

async function pullDevices() {
  devicesError.value = ''
  if (!isLoggedIn.value) {
    devicesError.value = '尚未登录：请先在「场景设置 → 项目连接」登录'
    return
  }
  const session = sceneStore.getConnectRuntimeSession()
  const svc = selectedService.value
  if (!session || !svc) {
    devicesError.value = '登录已过期或未选服务'
    return
  }
  loadingDevices.value = true
  try {
    const raw = await fetchMergedRaw(svc.sourceShape, session)
    const devices = enrichDevicesWithPoints(raw, svc.sourceShape)
    if (!devices.length) {
      devicesError.value = '连上了，但列表里没有设备'
      return
    }
    editorStore.setDevicesCache(svc.id, devices)
    if (!assetCode.value) assetCode.value = devices[0]!.assetCode
    // 当前对象若误用点号当 metricKey，拉取后立刻修好
    const cur = obj.value
    if (cur?.assetCode && cur.dataServiceId === svc.id) {
      const dev = deviceByCode(devices, cur.assetCode)
      if (dev?.points?.length) {
        const { object: fixed, changed, selectedPoints } = repairObjectBindingsToChineseNames(
          cur,
          svc.id,
          dev.points,
        )
        if (changed) {
          const newShape = ensureDeviceFieldMappings(
            svc.sourceShape,
            cur.assetCode,
            dev.deviceTypeValue,
            selectedPoints,
          )
          if (newShape !== svc.sourceShape) {
            sceneStore.upsertDataService({ ...svc, sourceShape: newShape })
          }
          props.onHistoryPush?.()
          sceneStore.updateObject(props.objectId, {
            infoCard: fixed.infoCard,
            dataBindings: fixed.dataBindings,
          })
        }
      }
    }
    devicePoints.value = seedFromCache(
      svc.id,
      assetCode.value,
      assetCode.value === boundAssetCode.value
        ? sceneStore.activeObjects().find((o) => o.id === props.objectId)?.dataBindings
        : undefined,
    )
    ElMessage.success(`拉到 ${devices.length} 台设备`)
  } catch (e) {
    devicesError.value = e instanceof Error ? e.message : String(e)
  } finally {
    loadingDevices.value = false
  }
}

function togglePoint(key: string, checked: boolean) {
  const idx = devicePoints.value.findIndex((p) => p.key === key)
  if (idx < 0) return
  if (checked && !devicePoints.value[idx]!.checked && selectedCount.value >= MAX_CARD_FIELDS) {
    ElMessage.warning(`卡片字段最多 ${MAX_CARD_FIELDS} 个，请先取消一个`)
    return
  }
  devicePoints.value = devicePoints.value.map((p, i) => (i === idx ? { ...p, checked: !!checked } : p))
}

function updatePointMeta(key: string, patch: Partial<{ displayName: string; unit?: string }>) {
  devicePoints.value = devicePoints.value.map((p) => (p.key === key ? { ...p, ...patch } : p))
}

function selectAll() {
  const un = devicePoints.value.filter((p) => !p.checked)
  const room = MAX_CARD_FIELDS - selectedCount.value
  if (room <= 0) {
    ElMessage.warning(`卡片字段已满 ${MAX_CARD_FIELDS} 个`)
    return
  }
  if (un.length > room) {
    ElMessage.warning(`最多勾选 ${MAX_CARD_FIELDS} 个，已只选前 ${room} 个`)
  }
  const toCheck = new Set(un.slice(0, room).map((p) => p.key))
  devicePoints.value = devicePoints.value.map((p) => (toCheck.has(p.key) ? { ...p, checked: true } : p))
}

function clearAll() {
  devicePoints.value = devicePoints.value.map((p) => ({ ...p, checked: false }))
}

/** 点击点位名/单位输入框时若未勾选，先自动勾上（省一次点击） */
function ensureChecked(key: string) {
  const p = devicePoints.value.find((x) => x.key === key)
  if (!p || p.checked) return
  if (selectedCount.value >= MAX_CARD_FIELDS) {
    ElMessage.warning(`卡片字段最多 ${MAX_CARD_FIELDS} 个，请先取消一个`)
    return
  }
  devicePoints.value = devicePoints.value.map((x) => (x.key === key ? { ...x, checked: true } : x))
}

/** 选中设备里已勾选的点位 → 统一指标条目（metricKey=中文名，三同一字符串） */
function selectedMetrics(): SelectedMetricEntry[] {
  return devicePoints.value
    .filter((p) => p.checked)
    .map((p) => ({
      metricKey: p.name,
      displayName: p.displayName || p.name,
      ...(p.unit ? { unit: p.unit } : {}),
    }))
}

function save() {
  const svc = selectedService.value
  if (!svc) {
    ElMessage.warning('请选择数据服务')
    return
  }
  if (!assetCode.value) {
    ElMessage.warning('请选择系统设备')
    return
  }
  if (!devicePoints.value.length) {
    ElMessage.warning('该设备还没有点位清单，请先点「拉取列表」')
    return
  }
  const checked = selectedMetrics()
  const built = buildInfoCardAndBindings(svc.id, assetCode.value, checked)
  const dataBindings: SceneObject['dataBindings'] = {
    ...(obj.value?.dataBindings ?? {}),
    infoCard: built.dataBindings.infoCard,
  }
  if (compDef.value?.supportsCrateFillBinding && crateFillMetricKey.value) {
    dataBindings.crateFill = {
      dataKey: buildLiveDataKey(svc.id, assetCode.value, crateFillMetricKey.value),
      metricKey: crateFillMetricKey.value,
      min: crateFillMin.value,
      max: crateFillMax.value,
    }
  } else {
    delete dataBindings.crateFill
  }

  // 把选中的真实点位登记回服务 fieldMappings（按设备类型/设备号匹配），运行时才能取到数
  const cachedDev = deviceByCode(editorStore.getDevicesCache(svc.id)?.devices, assetCode.value)
  const checkedRows = devicePoints.value.filter((p) => p.checked)
  const newShape = ensureDeviceFieldMappings(
    svc.sourceShape,
    assetCode.value,
    cachedDev?.deviceTypeValue,
    checkedRows.map((p) => ({ key: p.key, name: p.name, ...(p.unit ? { unit: p.unit } : {}) })),
  )

  props.onHistoryPush?.()
  if (newShape !== svc.sourceShape) {
    sceneStore.upsertDataService({ ...svc, sourceShape: newShape })
  }
  sceneStore.updateObject(props.objectId, {
    dataServiceId: svc.id,
    assetCode: assetCode.value,
    infoCard: built.infoCard,
    dataBindings,
  })
  ElMessage.success(`已保存绑定（${checked.length} 个点位）`)
  emit('close')
}

/** 清空绑定（解除服务/设备/卡片字段） */
function clearBinding() {
  const { assetCode: _ac, dataServiceId: _did, infoCard: _ic, dataBindings: _db, ...rest } = obj.value ?? {}
  void _ac, _did, _ic, _db
  props.onHistoryPush?.()
  sceneStore.updateObject(props.objectId, rest as Partial<SceneObject>)
  ElMessage.success('已解除绑定')
  emit('close')
}

const boundServiceLabel = computed(() => {
  const svc = services.value.find((s) => s.id === boundServiceId.value)
  return svc ? `${svc.name}` : ''
})
const boundDeviceLabel = computed(() => {
  const o = obj.value
  if (!o?.assetCode) return ''
  const cached = editorStore.getDevicesCache(boundServiceId.value)
  const d = cached?.devices.find((x) => x.assetCode === o.assetCode)
  return d ? `${d.name}（${d.assetCode}）` : o.assetCode
})
</script>

<template>
  <Teleport to="body">
    <div v-if="visible" class="dbd-mask" @click.self="emit('close')" />
    <aside v-if="visible" class="dbd" role="dialog" aria-labelledby="dbd-title">
      <header class="dbd-head">
        <h2 id="dbd-title">设备绑定</h2>
        <button type="button" class="dbd-x" aria-label="关闭" @click="emit('close')">×</button>
      </header>

      <div class="dbd-body">
        <!-- 已绑定摘要 -->
        <div v-if="boundServiceId && boundAssetCode" class="dbd-summary">
          <p><span class="dbd-summary-label">已对应</span>{{ boundServiceLabel }} → {{ boundDeviceLabel }}</p>
        </div>

        <!-- 未登录引导 -->
        <div v-if="!isLoggedIn" class="dbd-login-tip">
          <p>先在「项目连接」登录一次，之后选服务、选设备、绑字段都不用再填密码。</p>
          <button type="button" class="dbd-btn primary" @click="emit('openProjectConnect')">去项目连接</button>
        </div>

        <template v-else>
          <label class="dbd-field">
            <span>数据服务</span>
            <select v-model="serviceId" @change="onServiceChange">
              <option value="" disabled>请选择服务</option>
              <option v-for="s in services" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </label>

          <div v-if="selectedService" class="dbd-field">
            <span>系统设备</span>
            <div class="dbd-device-row">
              <select v-model="assetCode" @change="onAssetChange">
                <option value="" disabled>请选择设备</option>
                <option v-for="d in deviceOptions" :key="d.assetCode" :value="d.assetCode">{{ d.name }}（{{ d.assetCode }}）</option>
              </select>
              <button type="button" class="dbd-btn ghost" :disabled="loadingDevices" @click="pullDevices">
                {{ loadingDevices ? '拉取中…' : '拉取列表' }}
              </button>
            </div>
            <p v-if="!deviceOptions.length" class="dbd-muted">还没有设备列表，点「拉取列表」从对方系统拉一次。</p>
            <p v-if="devicesError" class="dbd-err">{{ devicesError }}</p>
          </div>

          <div v-if="selectedService && assetCode" class="dbd-metrics">
            <div class="dbd-metrics-head">
              <h3>卡片字段 <span class="dbd-count">{{ selectedCount }}/{{ MAX_CARD_FIELDS }}</span></h3>
              <div class="dbd-metrics-actions">
                <button type="button" class="dbd-btn ghost" @click="selectAll">全选</button>
                <button type="button" class="dbd-btn ghost" @click="clearAll">清空</button>
              </div>
            </div>
            <p class="dbd-muted">已按「{{ assetCode }}」的真实点位列出；勾选最多 {{ MAX_CARD_FIELDS }} 个显示在产品卡片上。</p>
            <ul class="dbd-metric-list">
              <li v-for="m in devicePoints" :key="m.key" class="dbd-metric-row">
                <input
                  type="checkbox"
                  :checked="m.checked"
                  @change="togglePoint(m.key, ($event.target as HTMLInputElement).checked)"
                />
                <input
                  class="dbd-inline-name"
                  :value="m.displayName"
                  placeholder="显示名"
                  @focus="ensureChecked(m.key)"
                  @input="updatePointMeta(m.key, { displayName: ($event.target as HTMLInputElement).value })"
                />
                <input
                  class="dbd-inline-unit"
                  :value="m.unit ?? ''"
                  placeholder="单位"
                  @focus="ensureChecked(m.key)"
                  @input="updatePointMeta(m.key, { unit: ($event.target as HTMLInputElement).value || undefined })"
                />
              </li>
            </ul>
            <p v-if="!devicePoints.length" class="dbd-muted">该设备没有可勾选的点位（可先点「拉取列表」）。</p>
          </div>

          <div v-if="selectedService && assetCode && compDef?.supportsCrateFillBinding" class="dbd-field">
            <span>货位占用率</span>
            <select v-model="crateFillMetricKey">
              <option value="">不绑定（保持静态占用率）</option>
              <option v-for="m in devicePoints" :key="m.key" :value="m.name">
                {{ m.displayName || m.name }}
              </option>
            </select>
            <div v-if="crateFillMetricKey" class="dbd-device-row">
              <input v-model.number="crateFillMin" class="dbd-inline-num" type="number" placeholder="空货架数值" />
              <input v-model.number="crateFillMax" class="dbd-inline-num" type="number" placeholder="装满数值" />
            </div>
            <p v-if="crateFillMetricKey" class="dbd-muted">
              数值 {{ crateFillMin }}（空）～{{ crateFillMax }}（满）线性映射为货架箱子点亮比例。
            </p>
          </div>
        </template>
      </div>

      <footer class="dbd-foot">
        <button v-if="boundAssetCode" type="button" class="dbd-btn ghost danger" @click="clearBinding">解除绑定</button>
        <span class="dbd-foot-spacer" />
        <button type="button" class="dbd-btn ghost" @click="emit('close')">取消</button>
        <button type="button" class="dbd-btn primary" :disabled="!isLoggedIn || !selectedService || !assetCode" @click="save">
          保存
        </button>
      </footer>
    </aside>
  </Teleport>
</template>

<style scoped>
.dbd-mask {
  position: fixed;
  inset: 0;
  z-index: 1350;
  background: rgba(0, 0, 0, 0.4);
}
.dbd {
  position: fixed;
  top: 56px;
  right: 16px;
  width: min(360px, 94vw);
  max-height: calc(100vh - 80px);
  z-index: 1360;
  display: flex;
  flex-direction: column;
  background: #111822;
  border: 1px solid #2c3f5a;
  border-radius: 10px;
  color: #e7eef8;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45);
  font-size: 12px;
  overflow: hidden;
}
.dbd-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid #2c3f5a;
  flex-shrink: 0;
}
.dbd-head h2 { margin: 0; font-size: 14px; font-weight: 600; color: #e7eef8; }
.dbd-x { border: none; background: transparent; color: #9bb0c9; font-size: 20px; line-height: 1; cursor: pointer; }
.dbd-x:hover { color: #e7eef8; }
.dbd-body { flex: 1; overflow: auto; padding: 10px 12px; display: flex; flex-direction: column; gap: 10px; }
.dbd-summary {
  padding: 8px 10px;
  background: #0b121c;
  border: 1px solid #24344a;
  border-radius: 6px;
}
.dbd-summary p { margin: 0; font-size: 12px; color: #c5d4e8; }
.dbd-summary-label { color: #64748b; margin-right: 6px; }
.dbd-login-tip {
  padding: 10px;
  background: rgba(30, 74, 45, 0.25);
  border: 1px solid rgba(63, 174, 119, 0.45);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}
.dbd-login-tip p { margin: 0; font-size: 12px; color: #9fe8c0; line-height: 1.45; }
.dbd-field { display: flex; flex-direction: column; gap: 4px; font-size: 11px; color: #9bb0c9; }
.dbd-field select {
  border: 1px solid #2c3f5a;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 12px;
  background: #0b121c;
  color: #e7eef8;
  color-scheme: dark;
}
.dbd-field select:focus { outline: none; border-color: #2f6aa8; }
.dbd-device-row { display: flex; gap: 6px; }
.dbd-device-row select { flex: 1; min-width: 0; }
.dbd-metrics h3 { margin: 0 0 6px; font-size: 12px; font-weight: 600; color: #c5d4e8; }
.dbd-metrics-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 4px; }
.dbd-metrics-head h3 { margin: 0; }
.dbd-count { color: #7fb2e5; font-weight: 600; font-size: 11px; margin-left: 4px; }
.dbd-metrics-actions { display: flex; gap: 4px; }
.dbd-metrics-actions .dbd-btn { padding: 2px 6px; font-size: 11px; }
.dbd-metric-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 4px; }
.dbd-metric-row {
  display: grid;
  grid-template-columns: 16px 1fr 64px;
  gap: 6px;
  align-items: center;
  padding: 4px 8px;
  background: #0b121c;
  border: 1px solid #24344a;
  border-radius: 6px;
}
.dbd-inline-num {
  flex: 1;
  min-width: 0;
  border: 1px solid #2c3f5a;
  border-radius: 6px;
  padding: 4px 7px;
  font-size: 12px;
  background: #0b121c;
  color: #e7eef8;
  color-scheme: dark;
}
.dbd-inline-num:focus { outline: none; border-color: #2f6aa8; }
.dbd-inline-name, .dbd-inline-unit {
  border: 1px solid #2c3f5a;
  border-radius: 6px;
  padding: 4px 7px;
  font-size: 12px;
  background: #0b121c;
  color: #e7eef8;
  color-scheme: dark;
}
.dbd-inline-name:focus, .dbd-inline-unit:focus { outline: none; border-color: #2f6aa8; }
.dbd-err { color: #f07178; font-size: 11px; margin: 4px 0 0; line-height: 1.4; }
.dbd-muted { color: #64748b; font-size: 11px; margin: 4px 0 0; line-height: 1.4; }
.dbd-foot {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 10px;
  border-top: 1px solid #2c3f5a;
  flex-shrink: 0;
  background: #111822;
}
.dbd-foot-spacer { flex: 1; }
.dbd-btn {
  border: 1px solid #2c3f5a;
  background: #182433;
  color: #d7e4f5;
  border-radius: 6px;
  padding: 5px 9px;
  cursor: pointer;
  font-size: 12px;
  white-space: nowrap;
}
.dbd-btn:hover:not(:disabled) { border-color: #3d5575; }
.dbd-btn.primary { background: #1f4b7a; border-color: #2f6aa8; color: #eff7ff; }
.dbd-btn.primary:hover:not(:disabled) { background: #255a90; }
.dbd-btn.ghost { background: transparent; }
.dbd-btn.danger { color: #ff8888; }
.dbd-btn:disabled { opacity: 0.45; cursor: not-allowed; }
</style>
